import { Trans } from "@lingui/macro";
import { IconButton } from "@mui/material";
import { UiDialogResponsive, UiLink, UiTypography } from "@smp/ui";
import { IconX } from "@tabler/icons-react";
import clsx from "clsx";
import { FC, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { PageNotFound } from "@/components/feedback/pages/page_not_found";
import { HelperAdapterLink } from "@/components/helpers/helper_adapter_link";
import { useQueryPolotnoSticker, useQueryPolotnoStickerpack } from "@/queries";
import { useContextStickers } from "@/screens/app_public/stickers/context";
import { useStickerMeta } from "@/screens/app_public/stickers/hooks";
import { customGeneratePath } from "@/utils";

import classes from "./sticker_dialog.module.css";
import { StickerHelmet } from "./sticker_helmet";
import { StickerInfo } from "./sticker_info";
import { StickerPreview } from "./sticker_preview";

export const StickerDialog: FC = () => {
  const navigate = useNavigate();

  const {
    pathPattern,
    state: { activeStickerId },
  } = useContextStickers();

  const backUrl = useMemo(() => customGeneratePath(pathPattern), [pathPattern]);

  const { data: stickerData, isLoading: isStickerLoading } =
    useQueryPolotnoSticker(activeStickerId);

  const { data: stickerpackData, isLoading: isStickerpackLoading } =
    useQueryPolotnoStickerpack(stickerData?.stickerpack);

  const meta = useStickerMeta(stickerData);

  const handleClose = useCallback(() => {
    navigate(customGeneratePath(pathPattern), {
      replace: true,
    });
  }, [navigate, pathPattern]);

  return (
    <UiDialogResponsive
      open={Boolean(activeStickerId)}
      disablePortal
      isButtonCloseHidden
      fullWidth
      onClose={handleClose}
      className={classes.dialog}
      classes={{
        dialogContent: classes.dialog__content,
        dialogTitle: classes.dialog__title,
        paper: clsx(
          classes.dialog__paper,
          !stickerData &&
            activeStickerId !== undefined &&
            classes.dialog__paper_404,
        ),
      }}
      isLoading={isStickerLoading || isStickerpackLoading}
    >
      <IconButton
        onClick={handleClose}
        className={classes.dialog__closeBtn}
        component={HelperAdapterLink}
        to={backUrl}
      >
        <IconX />
      </IconButton>

      {!stickerData && activeStickerId !== undefined ? (
        <PageNotFound />
      ) : (
        <>
          {meta && <StickerHelmet meta={meta} />}

          <div className={classes.container}>
            <div className={clsx(classes.scroller)}>
              <div className={classes.main}>
                <StickerPreview
                  src={stickerData?.image_preview_url}
                  meta={meta}
                />

                {stickerData && stickerpackData && (
                  <StickerInfo
                    sticker={stickerData}
                    stickerpack={stickerpackData}
                  />
                )}
              </div>

              {!!stickerpackData?.stickers.length && (
                <div className={classes.similar}>
                  <UiTypography className={classes.similar__title}>
                    <Trans>Similar</Trans>
                  </UiTypography>

                  <div className={classes.grid}>
                    {stickerpackData.stickers.map((sticker) => (
                      <UiLink
                        component={HelperAdapterLink}
                        key={sticker.id}
                        className={classes.grid__item}
                        to={customGeneratePath(pathPattern, {
                          stickerId: sticker.id,
                        })}
                      >
                        <img
                          className={classes.grid__item__img}
                          loading="lazy"
                          alt={String(sticker.id)}
                          src={sticker.image_preview_url}
                        />
                      </UiLink>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </UiDialogResponsive>
  );
};
